<template>
	<BasePlaceHolder :placehold="loading">
		<DataTable>
			<template #head>
				<DataTableHeaderHistoricalReturns @update-mobile-column="updateMobileColumn" />
			</template>
			<template #body>
				<div v-if="!appStore.isMobile">
					<ExpandableTableRow
						v-for="(yearlyReturn, index) in yearlyReturnsInReverseOrder"
						:key="index"
						:open-by-default="index == 0"
						:fade-header-content="true"
						:align-row-title="true"
					>
						<template #row-title>
							<span class="ml-100 body-xl-md">{{ yearlyReturn.year }}</span>
						</template>
						<template #row-title-content>
							<DataTableCell
								v-for="(month, i) in yearlyReturn.months"
								:key="month.monthOrdinal"
								class="reit-historical-performance body-xs-mdo"
								:class="{ 'reit-historical-performance-quarter-end': (i + 1) % 3 === 0 }"
							>
								{{ formattedPerformancePercentageValue(month.totalReturn, true, true) }}
							</DataTableCell>
						</template>
						<template #row-title-content-static>
							<DataTableCell
								v-for="i in yearlyReturn.months.length"
								:key="i"
								class="reit-historical-performance body-xs-mdo"
								:class="{ 'reit-historical-performance-quarter-end': i % 3 === 0 }"
							>
							</DataTableCell>
						</template>
						<template #row-expanded-content>
							<AnnualHistoricalReturnsTable :monthly-returns="yearlyReturn.months" />
						</template>
					</ExpandableTableRow>
				</div>
				<div v-else>
					<ExpandableTableRow
						v-for="(yearlyReturn, index) in yearlyReturnsInReverseOrder"
						:key="index"
						:open-by-default="index == 0"
						:fade-header-content="true"
						:no-left-padding="true"
					>
						<template #row-title>
							<span class="ml-100 mr-150 body-xl-md">{{ yearlyReturn.year }}</span>
						</template>
						<template #row-title-content>
							<DataTableCell
								v-for="month in getMonthlyPerformanceToDisplay(yearlyReturn)"
								:key="month.monthOrdinal"
								class="reit-historical-performance body-xs-mdo"
							>
								{{ formattedPerformancePercentageValue(month.totalReturn, true, true) }}
							</DataTableCell>
						</template>
						<template #row-expanded-content>
							<AnnualHistoricalReturnsTable
								:monthly-returns="getMonthlyPerformanceToDisplay(yearlyReturn)"
							/>
						</template>
					</ExpandableTableRow>
				</div>
			</template>
		</DataTable>
	</BasePlaceHolder>
</template>
<script setup lang="ts">
import { computed, Ref, ref, watch } from 'vue';
import { FundPerformanceForMonth, FundPerformanceForYear } from 'types/funds';
import AnnualHistoricalReturnsTable from '@components/offering-details/annual-historical-returns-table.vue';
import DataTable from '@components/account/data-table.vue';
import DataTableCell from '@components/account/data-table-cell.vue';
import DataTableHeaderHistoricalReturns from '@components/account/data-table-header-historical-returns.vue';
import ExpandableTableRow from '@components/account/expandable-table-row.vue';
import { formattedPerformancePercentageValue } from '@utils/performance-portfolio';
import { ScreenSizeType } from 'types/client';
import { useAppStore } from '@stores/app';

interface Props {
	yearlyReturns: Array<FundPerformanceForYear>;
}

const props = defineProps<Props>();

const appStore = useAppStore();

const activeQuarter = ref('all');
const yearlyReturnsWithYtd: Ref<Array<FundPerformanceForYear>> = ref([]);
const loading = ref(true);

const screenSize = computed((): ScreenSizeType => appStore.screenSize);

const yearlyReturnsInReverseOrder = computed(
	(): Array<FundPerformanceForYear> => yearlyReturnsWithYtd.value.slice().sort((a, b) => (a.year < b.year ? 1 : -1))
);

watch(
	() => screenSize.value,
	(newScreenSize: ScreenSizeType): void => {
		if (newScreenSize === 'MOBILE') {
			activeQuarter.value = 'ytd';
		}
	}
);

function updateMobileColumn(header: string): void {
	activeQuarter.value = header;
}

function getYtdNavAtMonthEnd(yearlyReturn: FundPerformanceForYear): string {
	const monthsWithPerformanceData = yearlyReturn.months.filter(
		(monthPerformance) => monthPerformance.navAtMonthEnd !== ''
	);
	return monthsWithPerformanceData[monthsWithPerformanceData.length - 1]?.navAtMonthEnd ?? '';
}

function getMonthlyPerformanceToDisplay(
	yearlyPerformanceWithYtd: FundPerformanceForYear
): Array<FundPerformanceForMonth> {
	switch (activeQuarter.value) {
		case 'all':
			return yearlyPerformanceWithYtd.months;
		case 'q1':
			return yearlyPerformanceWithYtd.months.slice(0, 3);
		case 'q2':
			return yearlyPerformanceWithYtd.months.slice(3, 6);
		case 'q3':
			return yearlyPerformanceWithYtd.months.slice(6, 9);
		case 'q4':
			return yearlyPerformanceWithYtd.months.slice(9, 12);
		case 'ytd':
			return yearlyPerformanceWithYtd.months.slice(12, 13);
		default:
			return yearlyPerformanceWithYtd.months;
	}
}

function setup() {
	props.yearlyReturns.forEach((annualReturn: FundPerformanceForYear) => {
		const lastMonthIndex = annualReturn.months.length - 1;
		const ytdData: FundPerformanceForMonth = {
			month: 'YTD',
			monthOrdinal: '12',
			navAtMonthEnd: getYtdNavAtMonthEnd(annualReturn),
			navReturn: annualReturn.months[lastMonthIndex].navYearToDateReturn,
			navYearToDateReturn: annualReturn.months[lastMonthIndex].navYearToDateReturn,
			dividendReturn: annualReturn.months[lastMonthIndex].dividendYearToDateReturn,
			dividendYearToDateReturn: annualReturn.months[lastMonthIndex].dividendYearToDateReturn,
			totalReturn: annualReturn.months[lastMonthIndex].totalYearToDateReturn,
			totalYearToDateReturn: annualReturn.months[lastMonthIndex].totalYearToDateReturn
		};
		const monthlyReturnsDataWithYtd = annualReturn.months.concat([ytdData]);
		yearlyReturnsWithYtd.value.push({ year: annualReturn.year, months: monthlyReturnsDataWithYtd });
	});

	if (appStore.isMobile) {
		activeQuarter.value = 'ytd';
	}
	loading.value = false;
}

setup();
</script>

<style lang="scss">
@use '../../styles/constants/colors.scss' as *;

.reit-historical-performance {
	width: 3.5rem;
}
@media screen and (min-width: 768px) {
	.reit-historical-performance {
		width: 3rem;
	}
}
@media screen and (min-width: 1200px) {
	.reit-historical-performance {
		width: 4rem;
	}
	.reit-historical-performance-quarter-end {
		width: 5rem;
		border-right: 1px solid token('info-background');
		padding-right: 1rem;
	}
}
</style>
